// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

import React from 'react';
import { GLYPH_VIEWBOX, GLYPH_STROKE, glyphClass } from './glyph';
import type { SymbolGlyphProps } from './glyph';
import { Leads } from './Leads';

/**
 * The rotating-machine frame every motor-family glyph shares.
 *
 * A circle, with the machine's mark inside — `G`, `M`, a pump's impeller
 * triangle, a fan's blades. The circle is the part that carries run state:
 * filled when running, plain outline when stopped, dashed outline when nothing
 * says. The mark inverts on the fill through `.sld-glyph__mark`, so the SVG
 * itself paints only `currentColor` (ADR-0007) and the theme decides the rest.
 *
 * Filled-means-running is the reading operators bring from ISA-101 style HMI
 * screens and from Ignition's own motor and pump symbols; the dashed outline
 * for "no reading" is ours (ADR-0019), chosen so missing data can never be
 * mistaken for a stopped machine.
 */
export const RotatingMachine = ({
  size = 18,
  run,
  leads,
  children,
}: SymbolGlyphProps & { children: React.ReactNode }) => (
  <svg
    className={glyphClass(run)}
    width={size}
    height={size}
    viewBox={GLYPH_VIEWBOX}
    fill="none"
    aria-hidden="true"
    focusable="false"
    data-sld-run={run}
  >
    <Leads top={3.5} bottom={20.5} leads={leads} />
    <circle
      className="sld-glyph__body"
      cx="12"
      cy="12"
      r="8.5"
      stroke="currentColor"
      strokeWidth={GLYPH_STROKE}
    />
    {children}
  </svg>
);

/** The letter inside a `G` or `M` machine. */
export const MachineLetter = ({ letter }: { letter: string }) => (
  <text
    className="sld-glyph__mark"
    x="12"
    y="16.2"
    textAnchor="middle"
    fontSize="10"
    fontWeight="500"
    fontFamily="'IBM Plex Mono', monospace"
    fill="currentColor"
  >
    {letter}
  </text>
);
